// CDEK city / PVZ pickers + delivery quote for checkout
import { apiFetchJSON } from '../../core/api.js';
import { escapeHtmlText, escapeAttr } from '../../core/format.js';
import { isBuildCheckoutMode } from '../../app/checkout-mode.js';
import { loadCart } from '../cart/cart.js';

export interface CdekCity {
  code: number;
  name: string;
  region?: string;
}

export interface CdekPvz {
  code: string;
  name: string;
  address: string;
}

export interface CdekQuote {
  deliveryRub: number;
  periodMin: number;
  periodMax: number;
  tariffCode?: number;
}

interface CdekCitiesResponse {
  ok: boolean;
  cities?: CdekCity[];
  error?: string;
}

interface CdekPvzResponse {
  ok: boolean;
  points?: CdekPvz[];
  error?: string;
}

interface CdekQuoteResponse {
  ok: boolean;
  quote?: CdekQuote;
  error?: string;
}

type SheetMode = 'city' | 'pvz';
type ChangeListener = () => void;

let configured = false;
let manualMode = true;
let cityCode: number | null = null;
let cityName = '';
let pvzCode = '';
let pvzLabel = '';
let quote: CdekQuote | null = null;
let quoteLoading = false;
let pvzCache: CdekPvz[] = [];
let sheetMode: SheetMode | null = null;
let citySearchTimer: ReturnType<typeof setTimeout> | null = null;
let pvzFilterTimer: ReturnType<typeof setTimeout> | null = null;
const listeners: ChangeListener[] = [];

function emitChange(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

export function onCheckoutCdekChange(fn: ChangeListener): void {
  listeners.push(fn);
}

export function isCheckoutCdekPickerMode(): boolean {
  return configured && !manualMode;
}

export function isCheckoutCdekQuoteLoading(): boolean {
  return quoteLoading;
}

export function getCheckoutCdekQuote(): CdekQuote | null {
  return quote;
}

export function getCheckoutCdekSnapshot(): {
  cityCode: number | null;
  pvzCode: string;
  deliveryRub: number | null;
} {
  return {
    cityCode,
    pvzCode,
    deliveryRub: quote?.deliveryRub ?? null
  };
}

function getCityHidden(): HTMLInputElement | null {
  const el = document.getElementById('checkout-city');
  return el instanceof HTMLInputElement ? el : null;
}

function getPickupHidden(): HTMLInputElement | null {
  const el = document.getElementById('checkout-pickup-point');
  return el instanceof HTMLInputElement ? el : null;
}

function getCityTrigger(): HTMLButtonElement | null {
  const el = document.getElementById('checkout-city-trigger');
  return el instanceof HTMLButtonElement ? el : null;
}

function getPickupTrigger(): HTMLButtonElement | null {
  const el = document.getElementById('checkout-pickup-trigger');
  return el instanceof HTMLButtonElement ? el : null;
}

function setTriggerLabel(
  btn: HTMLButtonElement | null,
  text: string,
  placeholder: boolean,
  error = false
): void {
  if (!btn) return;
  btn.textContent = text;
  btn.classList.toggle('is-placeholder', placeholder);
  btn.classList.toggle('form-input-error', error);
}

function syncHiddenFields(): void {
  const cityHidden = getCityHidden();
  const pickupHidden = getPickupHidden();
  if (cityHidden) cityHidden.value = cityName;
  if (pickupHidden) pickupHidden.value = pvzLabel;
}

function updatePickupTriggerState(): void {
  const btn = getPickupTrigger();
  if (!btn) return;
  if (!cityCode) {
    btn.disabled = true;
    setTriggerLabel(btn, 'Сначала выберите город', true);
    return;
  }
  btn.disabled = false;
  if (pvzLabel) {
    setTriggerLabel(btn, pvzLabel, false);
  } else {
    setTriggerLabel(btn, 'Выберите пункт выдачи', true);
  }
}

function renderQuoteNote(): void {
  const wrap = document.getElementById('checkout-shipping-quote-wrap');
  const note = document.getElementById('checkout-shipping-quote-note');
  const textEl = document.getElementById('checkout-shipping-quote-note-text');
  if (!wrap || !note || !textEl) return;

  if (isBuildCheckoutMode()) {
    wrap.hidden = true;
    note.classList.remove('is-open');
    return;
  }

  if (!isCheckoutCdekPickerMode() || !cityCode || !pvzCode) {
    wrap.hidden = true;
    note.classList.remove('is-open');
    return;
  }

  wrap.hidden = false;
  note.classList.add('is-open');

  if (quoteLoading) {
    textEl.textContent = 'Рассчитываем доставку…';
    return;
  }

  if (!quote) {
    textEl.textContent = 'Не удалось рассчитать доставку. Попробуйте другой ПВЗ или оформите заказ — уточним вручную.';
    return;
  }

  const days =
    quote.periodMin === quote.periodMax
      ? `${quote.periodMin} дн.`
      : `${quote.periodMin}–${quote.periodMax} дн.`;
  textEl.textContent = `Доставка СДЭК: ${quote.deliveryRub.toLocaleString('ru-RU')} ₽, ориентир ${days}`;
}

function applyModeUi(): void {
  const pickers = document.getElementById('checkout-cdek-pickers');
  const pickupPickers = document.getElementById('checkout-pickup-pickers');
  const manual = document.getElementById('checkout-cdek-manual');
  const pickupManual = document.getElementById('checkout-pickup-manual-wrap');
  const desc = document.getElementById('checkout-delivery-cdek-desc');

  const pickerOn = isCheckoutCdekPickerMode();
  if (pickers) pickers.hidden = !pickerOn;
  if (pickupPickers) pickupPickers.hidden = !pickerOn;
  if (manual) manual.hidden = pickerOn;
  if (pickupManual) pickupManual.hidden = pickerOn;
  if (desc && pickerOn) {
    desc.textContent = 'Доставка СДЭК до пункта выдачи. Стоимость рассчитывается после выбора ПВЗ.';
  }
}

export function resetCheckoutCdekSelection(): void {
  cityCode = null;
  cityName = '';
  pvzCode = '';
  pvzLabel = '';
  quote = null;
  quoteLoading = false;
  pvzCache = [];
  setTriggerLabel(getCityTrigger(), 'Выберите город', true);
  updatePickupTriggerState();
  syncHiddenFields();
  renderQuoteNote();
  emitChange();
}

function closeSheet(): void {
  const overlay = document.getElementById('checkout-cdek-sheet');
  if (overlay) overlay.hidden = true;
  sheetMode = null;
}

function setSheetLoading(loading: boolean): void {
  const overlay = document.getElementById('checkout-cdek-sheet');
  if (!overlay) return;
  overlay.classList.toggle('is-loading', loading);
  if (loading) {
    const list = document.getElementById('checkout-cdek-sheet-list');
    if (list) list.innerHTML = '';
  }
}

function renderSheetItems(html: string): void {
  const list = document.getElementById('checkout-cdek-sheet-list');
  if (list) list.innerHTML = html;
}

function openSheet(mode: SheetMode): void {
  const overlay = document.getElementById('checkout-cdek-sheet');
  const title = document.getElementById('checkout-cdek-sheet-title');
  const search = document.getElementById('checkout-cdek-sheet-search') as HTMLInputElement | null;
  if (!overlay || !title) return;

  sheetMode = mode;
  overlay.hidden = false;
  title.textContent = mode === 'city' ? 'Город доставки' : 'Пункт выдачи СДЭК';
  if (search) {
    search.value = '';
    search.placeholder = mode === 'city' ? 'Начните вводить город' : 'Поиск по адресу или названию';
    setTimeout(() => search.focus(), 80);
  }

  if (mode === 'city') {
    void searchCities('');
  } else {
    renderPvzList('');
  }
}

async function searchCities(query: string): Promise<void> {
  const q = String(query || '').trim();
  if (q.length === 1) {
    renderSheetItems('<p class="checkout-picker-empty">Введите минимум 2 символа</p>');
    return;
  }
  setSheetLoading(true);
  try {
    const url =
      q.length >= 2
        ? `/api/cdek/cities?q=${encodeURIComponent(q)}`
        : '/api/cdek/cities?q=моск';
    const res = await apiFetchJSON<CdekCitiesResponse>(url, { cache: 'no-store' });
    const cities = res?.ok && Array.isArray(res.cities) ? res.cities : [];
    if (!cities.length) {
      renderSheetItems('<p class="checkout-picker-empty">Город не найден</p>');
      return;
    }
    renderSheetItems(
      cities
        .map((city) => {
          const label = city.region ? `${city.name}, ${city.region}` : city.name;
          return (
            '<button type="button" class="checkout-picker-item" data-cdek-city="' +
            String(city.code) +
            '" data-cdek-city-name="' +
            escapeAttr(city.name) +
            '">' +
            '<span class="checkout-picker-item-lines">' +
            '<span class="checkout-picker-item-title">' +
            escapeHtmlText(city.name) +
            '</span>' +
            (city.region
              ? '<span class="checkout-picker-item-sub">' + escapeHtmlText(city.region) + '</span>'
              : '') +
            '</span></button>'
          );
        })
        .join('')
    );
  } catch {
    renderSheetItems('<p class="checkout-picker-empty">Не удалось загрузить города</p>');
  } finally {
    setSheetLoading(false);
  }
}

function renderPvzList(filter: string): void {
  const q = String(filter || '')
    .trim()
    .toLowerCase();
  const items = !q
    ? pvzCache
    : pvzCache.filter((p) => {
        const hay = `${p.name} ${p.address}`.toLowerCase();
        return hay.includes(q);
      });

  if (!items.length) {
    renderSheetItems(
      '<p class="checkout-picker-empty">' +
        (pvzCache.length ? 'Ничего не найдено' : 'В этом городе нет пунктов выдачи') +
        '</p>'
    );
    return;
  }

  renderSheetItems(
    items
      .map((pvz) => {
        const label = pvz.address ? `${pvz.code} — ${pvz.address}` : pvz.code || pvz.name;
        return (
          '<button type="button" class="checkout-picker-item" data-cdek-pvz="' +
          escapeAttr(pvz.code) +
          '" data-cdek-pvz-label="' +
          escapeAttr(label) +
          '">' +
          '<span class="checkout-picker-item-lines">' +
          '<span class="checkout-picker-item-title">' +
          escapeHtmlText(pvz.code) +
          '</span>' +
          (pvz.address
            ? '<span class="checkout-picker-item-sub">' + escapeHtmlText(pvz.address) + '</span>'
            : '') +
          '</span></button>'
        );
      })
      .join('')
  );
}

async function loadPvzForCity(code: number): Promise<void> {
  setSheetLoading(true);
  try {
    const res = await apiFetchJSON<CdekPvzResponse>(
      `/api/cdek/pvz?cityCode=${encodeURIComponent(String(code))}`,
      { cache: 'no-store' }
    );
    pvzCache = res?.ok && Array.isArray(res.points) ? res.points : [];
    renderPvzList('');
  } catch {
    pvzCache = [];
    renderSheetItems('<p class="checkout-picker-empty">Не удалось загрузить пункты выдачи</p>');
  } finally {
    setSheetLoading(false);
  }
}

async function refreshQuote(): Promise<void> {
  if (isBuildCheckoutMode()) {
    quote = null;
    quoteLoading = false;
    renderQuoteNote();
    emitChange();
    return;
  }

  if (!isCheckoutCdekPickerMode() || !cityCode || !pvzCode) {
    quote = null;
    quoteLoading = false;
    renderQuoteNote();
    emitChange();
    return;
  }

  const cart = loadCart();
  if (!cart.length) {
    quote = null;
    renderQuoteNote();
    emitChange();
    return;
  }

  quoteLoading = true;
  renderQuoteNote();
  emitChange();

  try {
    const res = await apiFetchJSON<CdekQuoteResponse>('/api/cdek/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cityCode,
        lines: cart.map((line) => ({ id: line.id, qty: line.qty || 1 }))
      })
    });
    quote = res?.ok && res.quote ? res.quote : null;
  } catch {
    quote = null;
  } finally {
    quoteLoading = false;
    renderQuoteNote();
    emitChange();
  }
}

function selectCity(code: number, name: string): void {
  const changed = cityCode !== code;
  cityCode = code;
  cityName = name;
  pvzCode = '';
  pvzLabel = '';
  quote = null;
  pvzCache = [];
  setTriggerLabel(getCityTrigger(), name, false);
  updatePickupTriggerState();
  syncHiddenFields();
  renderQuoteNote();
  closeSheet();
  emitChange();
  if (changed) {
    void loadPvzForCity(code);
  }
}

function selectPvz(code: string, label: string): void {
  pvzCode = code;
  pvzLabel = label;
  updatePickupTriggerState();
  syncHiddenFields();
  closeSheet();
  void refreshQuote();
}

function bindSheet(): void {
  const overlay = document.getElementById('checkout-cdek-sheet');
  const backdrop = document.getElementById('checkout-cdek-sheet-backdrop');
  const closeBtn = document.getElementById('checkout-cdek-sheet-close');
  const search = document.getElementById('checkout-cdek-sheet-search') as HTMLInputElement | null;
  const list = document.getElementById('checkout-cdek-sheet-list');

  backdrop?.addEventListener('click', closeSheet);
  closeBtn?.addEventListener('click', closeSheet);

  search?.addEventListener('input', () => {
    const q = String(search.value || '');
    if (sheetMode === 'city') {
      if (citySearchTimer) clearTimeout(citySearchTimer);
      citySearchTimer = setTimeout(() => {
        void searchCities(q);
      }, 280);
      return;
    }
    if (pvzFilterTimer) clearTimeout(pvzFilterTimer);
    pvzFilterTimer = setTimeout(() => {
      renderPvzList(q);
    }, 120);
  });

  list?.addEventListener('click', (ev) => {
    const target = (ev.target as HTMLElement | null)?.closest('[data-cdek-city], [data-cdek-pvz]');
    if (!(target instanceof HTMLElement)) return;
    if (target.dataset.cdekCity) {
      const code = Number(target.dataset.cdekCity);
      const name = String(target.dataset.cdekCityName || '').trim();
      if (Number.isFinite(code) && name) selectCity(code, name);
      return;
    }
    if (target.dataset.cdekPvz) {
      const code = String(target.dataset.cdekPvz || '').trim();
      const label = String(target.dataset.cdekPvzLabel || '').trim();
      if (code && label) selectPvz(code, label);
    }
  });
}

function bindPickers(): void {
  getCityTrigger()?.addEventListener('click', () => openSheet('city'));
  getPickupTrigger()?.addEventListener('click', () => {
    if (!cityCode) return;
    if (!pvzCache.length) {
      void loadPvzForCity(cityCode).then(() => openSheet('pvz'));
      return;
    }
    openSheet('pvz');
  });
}

export async function initCheckoutCdek(): Promise<void> {
  try {
    const res = await apiFetchJSON<CdekCitiesResponse>('/api/cdek/cities?q=мо', { cache: 'no-store' });
    configured = !!(res?.ok && Array.isArray(res.cities) && res.cities.length > 0);
    manualMode = !configured;
  } catch {
    configured = false;
    manualMode = true;
  }

  applyModeUi();
  bindSheet();
  bindPickers();
  resetCheckoutCdekSelection();
}

export function refreshCheckoutCdekQuote(): void {
  if (isBuildCheckoutMode()) return;
  if (!isCheckoutCdekPickerMode() || !cityCode || !pvzCode) return;
  void refreshQuote();
}
